import client from "./client";
import type {
  DashboardChangelogEntry,
  DashboardConfig,
  TileGrid,
  UserDashboard,
  UserReport,
  UserWorkspace,
  LibraryPage,
  PublicReportPage,
  ReportSubscriber,
  ScheduledPlacement,
} from "../types/reports";

export type ReportInput = {
  name?: string;
  description?: string | null;
  sql_query?: string | null;
  metric_id?: string | null;
  is_public?: boolean;
  dashboard_id?: string | null;
  pinned_to_dashboard?: boolean;
  dashboard_config?: DashboardConfig | null;
};

export type PinInput = { pinned: boolean; config?: DashboardConfig | null };

export type ScheduleInput = {
  label: string;
  cron_expression: string;
  is_active?: boolean;
  deliver_email?: boolean;
  deliver_slack?: boolean;
  recipient_emails?: string[];
  slack_channel_ids?: string[];
};

export type PreviewResult = { row_count: number; rows: Record<string, unknown>[]; error?: string | null };

export type WidgetProposal = {
  name: string;
  description?: string | null;
  sql_query: string;
  chart_type?: string;
  x_col?: string;
  y_col?: string;
};

export type DashboardChatResult = {
  type: string;
  message: string;
  widget?: WidgetProposal | null;
  preview?: PreviewResult | null;
};

export type BrianConversationTurn = {
  role: "user" | "assistant";
  content: string;
};

export type GeneratedReportProposal = {
  name: string;
  description?: string | null;
  sql_query: string;
  is_public?: boolean;
};

export type ReportChatResult = {
  type: "report_proposal" | "clarification" | "not_found";
  message: string;
  report?: GeneratedReportProposal | null;
  preview?: PreviewResult | null;
};

export const userReportsApi = {
  list: () => client.get<UserReport[]>("/my-reports"),
  get: (id: string) => client.get<UserReport>(`/my-reports/${id}`),
  create: (data: ReportInput) => client.post<UserReport>("/my-reports", data),
  update: (id: string, data: ReportInput) => client.put<UserReport>(`/my-reports/${id}`, data),
  delete: (id: string) => client.delete(`/my-reports/${id}`),
  preview: (id: string) => client.post<PreviewResult>(`/my-reports/${id}/preview`, undefined, { suppressErrorToast: true }),

  // Public discovery and subscriptions
  discover: (params?: { search?: string; page?: number; page_size?: number }) =>
    client.get<PublicReportPage>("/my-reports/discover", { params }),
  subscribe: (id: string) => client.put(`/my-reports/${id}/subscription`),
  unsubscribe: (id: string) => client.delete(`/my-reports/${id}/subscription`),
  listSubscribers: (id: string) =>
    client.get<ReportSubscriber[]>(`/my-reports/${id}/subscribers`),
  listSharingUsers: () =>
    client.get<Array<{ id: string; full_name: string; email: string }>>("/my-reports/sharing-users"),

  // Sharing
  share: (id: string, userId: string) =>
    client.post(`/my-reports/${id}/shares`, { user_id: userId }),
  removeShare: (id: string, shareId: string) =>
    client.delete(`/my-reports/${id}/shares/${shareId}`),

  // Schedule
  upsertSchedule: (id: string, data: ScheduleInput) =>
    client.put(`/my-reports/${id}/schedule`, data),
  deleteSchedule: (id: string) => client.delete(`/my-reports/${id}/schedule`),

  // Dashboard tiles
  listPinned: (dashboardId: string) =>
    client.get<UserReport[]>(`/my-reports/dashboard/pinned?dashboard_id=${dashboardId}`),
  pin: (id: string, data: PinInput) => client.patch<UserReport>(`/my-reports/${id}/pin`, data),

  // Named dashboards
  listDashboards: () => client.get<UserDashboard[]>("/my-reports/dashboards"),
  createDashboard: (name: string) => client.post<UserDashboard>("/my-reports/dashboards", { name }),
  renameDashboard: (id: string, name: string) =>
    client.patch<UserDashboard>(`/my-reports/dashboards/${id}`, { name }),
  reorderDashboards: (ids: string[]) =>
    client.post("/my-reports/dashboards/reorder", { ids }),
  deleteDashboard: (id: string) => client.delete(`/my-reports/dashboards/${id}`),
  generateDashboard: (name: string, description: string) =>
    client.post<UserDashboard>("/my-reports/dashboards/generate", { name, description }),
  dashboardChangelog: (id: string) =>
    client.get<DashboardChangelogEntry[]>(`/my-reports/dashboards/${id}/changelog`),

  // Ad-hoc SQL execution (for chat download)
  runSql: (sqlQuery: string, config?: import("axios").AxiosRequestConfig) =>
    client.post<PreviewResult>("/my-reports/run-sql", { sql_query: sqlQuery }, config),

  // Standalone report builder (Brian AI)
  reportChat: (message: string, conversation: BrianConversationTurn[] = []) =>
    client.post<ReportChatResult>("/my-reports/chat", { message, conversation }),

  // Dashboard chat (Brian AI)
  dashboardChat: (dashboardId: string, message: string, conversation: unknown[] = []) =>
    client.post<DashboardChatResult>(`/my-reports/dashboards/${dashboardId}/chat`, {
      message,
      conversation,
    }),

  // Tile grid layout persistence (drag/resize)
  updateLayout: (id: string, grid: TileGrid) =>
    client.patch<UserReport>(`/my-reports/${id}/layout`, { grid }, { suppressErrorToast: true }),

  // Workspaces
  listWorkspaces: () => client.get<UserWorkspace[]>("/my-reports/workspaces"),
  createWorkspace: (name: string) => client.post<UserWorkspace>("/my-reports/workspaces", { name }),
  renameWorkspace: (id: string, name: string) => client.patch<UserWorkspace>(`/my-reports/workspaces/${id}`, { name }),
  deleteWorkspace: (id: string) => client.delete(`/my-reports/workspaces/${id}`),
  reorderWorkspaces: (ids: string[]) => client.post("/my-reports/workspaces/reorder", { ids }),

  // Dashboard update (replaces renameDashboard — keep renameDashboard as an alias for backwards compat)
  updateDashboard: (id: string, data: { name?: string; workspace_id?: string | null; is_shared?: boolean }) =>
    client.patch<UserDashboard>(`/my-reports/dashboards/${id}`, data),

  // Library
  listLibrary: (params?: { search?: string; owner?: string; team?: string; page?: number; page_size?: number }) =>
    client.get<LibraryPage>("/my-reports/library", { params }),
  forkLibraryDashboard: (id: string, workspaceId?: string | null) =>
    client.post<UserDashboard>(`/my-reports/library/${id}/fork`, workspaceId ? { workspace_id: workspaceId } : {}),

  // Scheduled placements
  getScheduledPlacements: () => client.get<ScheduledPlacement[]>("/my-reports/scheduled-placements"),
  updateScheduledPlacement: (scheduledId: number, data: { workspace_id: string | null; position: number }) =>
    client.put<ScheduledPlacement>(`/my-reports/scheduled-placements/${scheduledId}`, data),
};
